import { useEffect } from "react";

/**
 * TradeDeskPixel Component
 * 
 * A reusable component for integrating The Trade Desk Universal Pixel tracking.
 * This component loads the Trade Desk conversion tracking script and initializes
 * the tracking with the provided advertiser ID and pixel IDs.
 * 
 * @param {string} advertiserId - The Trade Desk advertiser ID (e.g., "etpz76y")
 * @param {string[]} pixelIds - Array of pixel IDs to track (e.g., ["4i8jc6z"])
 * 
 * @example
 * <TradeDeskPixel advertiserId="etpz76y" pixelIds={["4i8jc6z"]} />
 * 
 * To enable this component globally, set REACT_APP_TRADEDESK_PIXEL=true in your .env file
 * and add conditional logic in Tracking.js to show the pixel on specific pages.
 */
const TradeDeskPixel = ({ advertiserId, pixelIds }) => {
  useEffect(() => {
    if (!advertiserId || !pixelIds || pixelIds.length === 0) return;

    // Initialize the Trade Desk Conversion Events Layer
    window.ttdConversionEventsLayer = window.ttdConversionEventsLayer || [];
    
    // Define the global ttdConversionEvents function if it doesn't exist
    if (!window.ttdConversionEvents) {
      window.ttdConversionEvents = function() {
        window.ttdConversionEventsLayer.push(arguments);
      };
    }

    // Load the Trade Desk Universal Pixel script
    const script = document.createElement("script");
    script.src = "https://js.adsrvr.org/up_loader.3.0.0.js";
    script.async = true;
    script.setAttribute("data-tradedesk-pixel", "true"); // Add marker for cleanup
    document.body.appendChild(script);

    // Fire tracking events only after the script has loaded
    script.onload = function () {
      window.ttdConversionEvents("init", {
        advertiserId: advertiserId,
        pixelIds: pixelIds
      });
      window.ttdConversionEvents("event", {});
    };
    // Cleanup function to remove the script
    return () => {
      // Remove the specific script we added
      const scripts = document.querySelectorAll('script[data-tradedesk-pixel="true"]');
      scripts.forEach((s) => {
        if (s.parentNode) {
          s.parentNode.removeChild(s);
        }
      });
    };
  }, [advertiserId, JSON.stringify(pixelIds)]);

  return null;
};

export default TradeDeskPixel;
